import { tuple } from 'src/replicant/utils/types';
import {
  AdultSpecies,
  PetSpeciesID,
  PetState,
  StarterSpecies,
} from 'src/replicant/pet/pet.ruleset';
import { GiftCreativeType } from '../../creatives/gift';
import { PartialRecord } from '@play-co/timestep-core';

export const animationState = tuple(
  'hang',
  'sleep',
  'eat',
  'toilet',
  'bath',
  'play',
  'angry',
  'joy',
  'surprised',
  'cry',
  'waive',
  'anxious',
  'evolve',
  'idle',
  'ascension',
);
export type PetAnimation = (typeof animationState)[number];

export const duoAnimationState = tuple(
  'invited',
  'inviteNormal',
  'inviteUltimate',
  'accepted',
  'refusedNormal',
  'refusedUltimate',
  'rocketIdle',
  'rocketLaunch',
  'shardIdle',
  'shardBox',
);
export type AdultAnimation = (typeof duoAnimationState)[number];
export type AnimationState = PetAnimation | AdultAnimation;

export const petAnimations: Record<
  PetSpeciesID,
  { url: string; scale: number }
> = {
  ball: {
    url: getSpeciesAnimationPath('ball'),
    scale: 0.79,
  },
  crab: {
    url: getSpeciesAnimationPath('crab'),
    scale: 0.79,
  },
  dog: {
    url: getSpeciesAnimationPath('dog'),
    scale: 0.79,
  },
  starball: {
    url: getSpeciesAnimationPath('starball'),
    scale: 0.79,
  },
  hamster: {
    url: getSpeciesAnimationPath('hamster'),
    scale: 0.79,
  },
  turtle: {
    url: getSpeciesAnimationPath('turtle'),
    scale: 0.79,
  },
  squirrel: {
    url: getSpeciesAnimationPath('squirrel'),
    scale: 0.79,
  },
  seaotter: {
    url: getSpeciesAnimationPath('seaotter'),
    scale: 0.79,
  },
  cat: {
    url: getSpeciesAnimationPath('cat'),
    scale: 0.79,
  },
  whale: {
    url: getSpeciesAnimationPath('whale'),
    scale: 0.79,
  },
  giraffe: {
    url: getSpeciesAnimationPath('giraffe'),
    scale: 0.79,
  },
  dinosaur: {
    url: getSpeciesAnimationPath('dinosaur'),
    scale: 0.79,
  },
  fish: {
    url: getSpeciesAnimationPath('fish'),
    scale: 0.79,
  },
  pig: {
    url: getSpeciesAnimationPath('pig'),
    scale: 0.79,
  },
  pinkdog: {
    url: getSpeciesAnimationPath('pinkdog'),
    scale: 0.79,
  },
  panda: {
    url: getSpeciesAnimationPath('panda'),
    scale: 0.79,
  },
  bear: {
    url: getSpeciesAnimationPath('bear'),
    scale: 0.79,
  },
  penguin: {
    url: getSpeciesAnimationPath('penguin'),
    scale: 0.79,
  },
  lion: {
    url: getSpeciesAnimationPath('lion'),
    scale: 0.79,
  },
  elephant: {
    url: getSpeciesAnimationPath('elephant'),
    scale: 0.79,
  },
  reindeer: {
    url: getSpeciesAnimationPath('reindeer'),
    scale: 0.79,
  },
  polarbear: {
    url: getSpeciesAnimationPath('polarbear'),
    scale: 0.79,
  },
  alpaca: {
    url: getSpeciesAnimationPath('alpaca'),
    scale: 0.79,
  },
  tiger: {
    url: getSpeciesAnimationPath('tiger'),
    scale: 0.79,
  },
  sealion: {
    url: getSpeciesAnimationPath('sealion'),
    scale: 0.79,
  },
  flamingo: {
    url: getSpeciesAnimationPath('flamingo'),
    scale: 0.79,
  },
  cow: {
    url: getSpeciesAnimationPath('cow'),
    scale: 0.79,
  },
  persiancat: {
    url: getSpeciesAnimationPath('persiancat'),
    scale: 0.79,
  },
  dolphin: {
    url: getSpeciesAnimationPath('dolphin'),
    scale: 0.79,
  },
};

export const petCreativesMap: Record<
  GiftCreativeType,
  Record<PetSpeciesID, string>
> = {
  gift: {
    ball: 'giftsent_ball.png',
    crab: 'giftsent_crab.png',
    dog: 'giftsent_dog.png',
    starball: 'giftsent_starball.png',
    hamster: 'giftsent_hamster.png',
    turtle: 'giftsent_turtle.png',
    squirrel: 'giftsent_squirrel.png',
    seaotter: 'giftsent_seaotter.png',
    cat: 'giftsent_cat.png',
    whale: 'giftsent_whale.png',
    giraffe: 'giftsent_giraffe.png',
    dinosaur: 'giftsent_dino.png',
    fish: 'giftsent_fish.png',
    pig: 'giftsent_pig.png',
    pinkdog: 'giftsent_pinkdog.png',
    panda: 'giftsent_panda.png',
    bear: 'giftsent_bear.png',
    penguin: 'giftsent_penguin.png',
    lion: 'giftsent_lion.png',
    elephant: 'giftsent_elephant.png',
    reindeer: 'giftsent_reindeer.png',
    polarbear: 'giftsent_polarbear.png',
    alpaca: 'giftsent_alpaca.png',
    tiger: 'giftsent_tiger.png',
    sealion: 'giftsent_sealion.png',
    flamingo: 'giftsent_flamingo.png',
    cow: 'giftsent_cow.png',
    persiancat: 'giftsent_persiancat.png',
    dolphin: 'giftsent_dolphin.png',
  },
};

type AnimationNames = Record<
  Exclude<PetState, 'sleeping'>,
  Record<'walking' | 'idle', string>
> &
  Record<PetAnimation, string>;

export type AdultAnimationNames = AnimationNames &
  Record<AdultAnimation, string>;

/// OLD
// Record<PetSpeciesID, AnimationNames> & Record<AdultSpecies, Record<AdultAnimation, string>>

const starterPetAnimationNames: Record<StarterSpecies, AnimationNames> = {
  ball: createAnimation('ball'),
  crab: createAnimation('crab'),
  dog: createAnimation('doggish'),
  starball: createAnimation('starball'),
  hamster: createAnimation('hamster'),
  turtle: createAnimation('turtle'),
  squirrel: createAnimation('squirrel'),
  seaotter: createAnimation('seaotter'),
  cat: createAnimation('cat'),
  pig: createAnimation('pig'),
  fish: createAnimation('fish'),
  bear: createAnimation('bear'),
  penguin: createAnimation('penguin'),
  sealion: createAnimation('sealion'),
};

const adultPetAnimationNames: Record<AdultSpecies, AdultAnimationNames> = {
  whale: createAdultAnimation('whale'),
  giraffe: createAdultAnimation('giraffe'),
  dinosaur: createAdultAnimation('dino'),
  panda: createAdultAnimation('panda'),
  pinkdog: createAdultAnimation('pinkdog'),
  lion: createAdultAnimation('lion'),
  elephant: createAdultAnimation('elephant'),
  reindeer: createAdultAnimation('reindeer'),
  polarbear: createAdultAnimation('polarbear'),
  alpaca: createAdultAnimation('alpaca'),
  tiger: createAdultAnimation('tiger'),
  flamingo: createAdultAnimation('flamingo'),
  cow: createAdultAnimation('cow'),
  persiancat: createAdultAnimation('persiancat'),
  dolphin: createAdultAnimation('dolphin'),
};

export const petAnimationNames = {
  ...starterPetAnimationNames,
  ...adultPetAnimationNames,
};

function createAnimation(name: string) {
  return {
    ascension: `${name}_ascension`,
    hang: `${name}_hanging`,
    sleep: `${name}_sleep`,
    eat: `${name}_eat`,
    toilet: `${name}_toilet`,
    bath: `${name}_bath`,
    play: `${name}_play`,
    angry: `${name}_angry`,
    joy: `${name}_joy`,
    evolve: `${name}_idle_happy`,
    surprised: `${name}_surprised`,
    cry: `${name}_cry`,
    waive: `${name}_waive`,
    anxious: `${name}_anxious`,
    idle: `${name}_idle_normal`,
    normal: {
      walking: `${name}_walk_normal`,
      idle: `${name}_idle_normal`,
    },
    happy: {
      walking: `${name}_walk_happy`,
      idle: `${name}_idle_happy`,
    },
    evolving: {
      walking: `${name}_walk_happy`,
      idle: `${name}_idle_happy`,
    },
    hungry: {
      walking: `${name}_walk_sad`,
      idle: `${name}_idle_sad`,
    },
    lonely: {
      walking: `${name}_walk_sad`,
      idle: `${name}_idle_sad`,
    },
    sad: {
      walking: `${name}_walk_sad`,
      idle: `${name}_idle_sad`,
    },
    very_hungry: {
      walking: `${name}_walk_verysad`,
      idle: `${name}_idle_verysad`,
    },
    very_lonely: {
      walking: `${name}_walk_verysad`,
      idle: `${name}_idle_verysad`,
    },
    very_sad: {
      walking: `${name}_walk_verysad`,
      idle: `${name}_idle_verysad`,
    },
    dirty: {
      walking: `${name}_walk_sad`,
      idle: `${name}_idle_sad`,
    },
    pee: {
      walking: `${name}_walk_sad`,
      idle: `${name}_idle_sad`,
    },
    very_dirty: {
      walking: `${name}_walk_verysad`,
      idle: `${name}_idle_verysad`,
    },
    very_pee: {
      walking: `${name}_walk_verysad`,
      idle: `${name}_idle_verysad`,
    },
    want_to_go_out: {
      walking: `${name}_walk_sad`,
      idle: `${name}_idle_sad`,
    },
    want_to_add_friend: {
      walking: `${name}_walk_sad`,
      idle: `${name}_idle_sad`,
    },
    sick: {
      walking: `${name}_walk_verysad`,
      idle: `${name}_idle_verysad`,
    },
    tooth_decay: {
      walking: `${name}_walk_verysad`,
      idle: `${name}_idle_verysad`,
    },
    scared: {
      walking: `${name}_walk_verysad`,
      idle: `${name}_idle_verysad`,
    },
    dying: {
      walking: `${name}_walk_semizombie`,
      idle: `${name}_idle_semizombie`,
    },
    dead: {
      walking: `${name}_walk_zombie`,
      idle: `${name}_idle_zombie`,
    },
  };
}

function createAdultAnimation(name: string) {
  return {
    ...createAnimation(name),
    accepted: `${name}_accepted`,
    refusedNormal: `${name}_refused_normalCoffee`,
    refusedUltimate: `${name}_refused_ulimateCoffee`,
    inviteNormal: `${name}_invite_normalCoffee`,
    inviteUltimate: `${name}_invite_ultimateCoffee`,
    invited: `${name}_invited`,
    rocketIdle: `${name}_rocket_idle`,
    rocketLaunch: `${name}_rocket_launch`,
    shardIdle: `${name}_shard_idle`,
    shardBox: `${name}_shard_box`,
  };
}

export const getUnscaledAnimations = (petAnimation: string) => {
  const animations: PartialRecord<'back' | 'front', string> = {};

  if (petAnimation.endsWith('_bath')) {
    animations.front = `${petAnimation}_front`;
  }

  return animations;
};

function getSpeciesAnimationPath(species: PetSpeciesID) {
  return `assets/species/${species}/movieclip`;
}
